import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { PageStructure } from "./pageStructure";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import { LocaleContext } from "./layout";
import useTranslations from "./useTranslations";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import { SkiDataView } from "./skidata";

const instagram = "https://www.instagram.com/3poles.fi/";
// Set to undefined to disable showing the email link
// const greetingsTo = "3poles@teelahti.fi";
const greetingsTo = undefined;

export const BlogFrontpage = ({ posts, title, children }) => {
  const { desktopImg, mobileImg } = useStaticQuery(graphql`
    query HeaderQuery {
      desktopImg: file(
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "northpole/dsc-3398.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 1900, height: 768)
        }
      }
      mobileImg: file(
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "northpole/dsc-3398.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 768, height: 768)
        }
      }
    }
  `);
  const sources = withArtDirection(getImage(desktopImg), [
    { media: `(max-width: 768px)`, image: getImage(mobileImg) },
  ]);

  // Consider replacing header image with map and fallback to image.
  return (
    <>
      <GatsbyImage image={sources} alt={title} />
      <PageStructure showSponsors={true}>
        <h1 className="title is-sr-only">{title}</h1>
        <div className="message  is-medium">
          <div className="message-body">{children}</div>
        </div>

        <BlogPosts posts={posts.nodes} />
      </PageStructure>
    </>
  );
};

export const BlogPost = ({
  title,
  date,
  href,
  body,
  img,
  data,
  summary = false,
}) => (
  <LocaleContext.Consumer>
    {({ locale }) => (
      <article itemScope itemType="http://schema.org/Article" className="mb-5">
        <header className="mb-3">
          <h1 className="title">
            <a href={href} rel="bookmark">
              {title}
            </a>
          </h1>
          <div className="subtitle is-6">{date.toLocaleDateString(locale)}</div>
        </header>
        <section
          className="blogbody block"
          dangerouslySetInnerHTML={{ __html: body }}
        />
        {summary ? (
          false
        ) : (
          <>
            <section className="block">
              {data ? <SkiDataView data={data} /> : false}
            </section>
            <section className="block has-text-weight-semibold">
              <SendGreetings to={greetingsTo} />
              <SocialMedia />
            </section>
            {img?.path ? <Img src={img.path} alt={img.alt} /> : false}
          </>
        )}
      </article>
    )}
  </LocaleContext.Consumer>
);

const SendGreetings = ({ to }) =>
  to ? (
    <Addendum href={`mailto:${to}`} txtKey="sendGreetings">
      <EmailOutlinedIcon />
    </Addendum>
  ) : (
    false
  );

const SocialMedia = () => (
  <Addendum href={instagram} txtKey="followInstagram">
    <InstagramIcon />
  </Addendum>
);

const Addendum = ({ txtKey, href, children }) => {
  const t = useTranslations();
  return (
    <div className="icon-text pb-1">
      <span className="icon">{children}</span>
      <span>
        <a href={href}>{t[txtKey]}</a>
      </span>
    </div>
  );
};

const Img = ({ src, alt }) => (
  <div className="block blogimg container">
    <figure>
      <img src={src} alt={alt} />
    </figure>
  </div>
);

export const BlogPosts = ({ posts }) => (
  <>
    {posts.map(p => (
      <BlogPost
        title={p.frontmatter.title}
        date={new Date(p.frontmatter.date)}
        href={p.path.slice(0, -1)}
        body={p.excerpt}
        summary
      />
    ))}
  </>
);

export const query = graphql`
  fragment PostInformation on MarkdownRemark {
    html
    excerpt(pruneLength: 260)
    frontmatter {
      title
      date
    }
    path: gatsbyPath(
      filePath: "/{MarkdownRemark.frontmatter__prefix}{MarkdownRemark.frontmatter__date}"
    )
  }
`;
